import { IconName } from '@infotech-soe/erp-ui-kit';
import { Nullable } from '@infotech-soe/merps/types/common';
import {
  AbstractActionM,
  ActionName,
  ActionType,
  ActionWithConfirmation,
  CategoryActionP,
  CategoryName,
  ConditionSetP,
  ExecutedActionP,
  IContextMenuActionItem,
  IContextMenuGroupItem,
  IContextMenuInteractItem,
  IContextMenuItem,
  IContextMenuItemOverride,
  PrintActionP,
  RegularActionP,
  SignActionP,
  TApplicationName,
  TContextMenuItemKind,
  tg,
} from '@infotech-soe/metaschema';

import { SIGN_ACTION_TYPE } from '../../helpers/constants';
import CachedSchemaRepository from '../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../infrastructure/schemas/storage/ISchemaRepository';
import CategoryViewModel, {
  CategoryTreeType,
} from '../category/view-model/CategoryViewModel';
import { ClientContext } from '../context/IContextService';
import { DatasetCurrentState } from '../dataset/view-model/DatasetCurrentState';
import IIntlService from '../intl/IIntlService';
import IntlService from '../intl/IntlService';
import UniqueId from '../uid/UniqueId';

import CommandComponent from './command-items/CommandComponent';
import CommandItem from './command-items/CommandItem';
import GroupItem from './command-items/GroupItem';
import SeparatorItem from './command-items/SeparatorItem';
import CommandDecorator from './CommandDecorator';
import ActionHistoryCommandImpl from './impl/ActionHistoryCommandImpl';
import CreateRecordCommandImpl from './impl/CreateRecordCommandImpl';
import DeleteRecordCommandImpl from './impl/DeleteRecordCommandImpl';
import DownloadCommandImpl from './impl/DownloadCommandImpl';
import FileLinkRecordCommandImpl from './impl/FileLinkRecordCommandImpl';
import FilterRecordsCommandImpl from './impl/FilterRecordsCommandImpl';
import LinkedFilesCommandImpl from './impl/LinkedFilesCommandImpl';
import MoveRecordCommandImpl from './impl/MoveRecordCommandImpl';
import NoopCommandImpl from './impl/NoopCommandImpl';
import PivotReportCommandImpl from './impl/PivotReportCommandImpl';
import PrintCommandImpl from './impl/PrintCommandImpl';
import ReportsCommandImpl from './impl/ReportsCommandImpl';
import ReviewRecordCommandImpl from './impl/ReviewRecordCommandImpl';
import RunActionCommandImpl from './impl/RunActionCommandImpl';
import SettingsCommandImpl from './impl/SettingsCommandImpl';
import ShowCategoryCommandImpl from './impl/ShowCategoryCommandImpl';
import SignCommandImpl from './impl/SignCommandImpl';
import ActionHistoryCommand from './wrap/ActionHistoryCommand';
import CatalogsCommand from './wrap/CatalogsCommand';
import CategoryCommand from './wrap/CategoryCommand';
import ClearColumnsFiltersCommand from './wrap/ClearColumnsFiltersCommand';
import CopyCommand from './wrap/CopyCommand';
import CreateRecordCommand from './wrap/CreateRecordCommand';
import DeleteRecordCommand from './wrap/DeleteRecordCommand';
import DocLinksGraphCommand from './wrap/DocLinksGraphCommand';
import DownloadCommand from './wrap/DownloadCommand';
import DuplicateRecordCommand from './wrap/DuplicateRecordCommand';
import EditRecordCommand from './wrap/EditRecordCommand';
import ExcelExportCommand from './wrap/ExcelExportCommand';
import CSVExportCommand from './wrap/ExportCommand';
import FileLinkRecordCommand from './wrap/FileLinkRecordCommand';
import FilterByColumnCommand from './wrap/FilterByColumnCommand';
import FilterRecordsCommand from './wrap/FilterRecordsCommand';
import InputDocsCommand from './wrap/InputDocsCommand';
import LinkedFilesCommand from './wrap/LinkedFilesCommand';
import MoveRecordCommand from './wrap/MoveRecordCommand';
import { OpenFactWorkTableCommand } from './wrap/OpenFactWorkTableCommand';
import { OpenPlanWorkTableCommand } from './wrap/OpenPlanWorkTableCommand';
import { OpenRolePrivsCommand } from './wrap/OpenRolePrivsCommand';
import { OpenTimeBoardCommand } from './wrap/OpenTimeBoardCommand';
import { OpenWorkCalendarCommand } from './wrap/OpenWorkCalendarCommand';
import OutputDocsCommand from './wrap/OutputDocsCommand';
import PivotCommand from './wrap/PivotCommand';
import PrintCommand from './wrap/PrintCommand';
import ReportsCommand from './wrap/ReportsCommand';
import RestoreLinksCommand from './wrap/RestoreLinksCommand';
import ReviewRecordCommand from './wrap/ReviewRecordCommand';
import RunActionCommand from './wrap/RunActionCommand';
import SettingsCommand from './wrap/SettingsCommand';
import ShowCategoryCommand from './wrap/ShowCategoryCommand';
import SignCommand from './wrap/SignCommand';
import ToolbarCommand from './wrap/ToolbarCommand';
import UpdateDatasetCommand from './wrap/UpdateDatasetCommand';
import WatchCommand from './wrap/WatchCommand';
import WeakenLinksCommand from './wrap/WeakenLinksCommand';

export type IReviewActionParsed = AbstractActionM & {
  Type: 'Review';
  ProcessMode: 'Current';
};

export interface NamedAction {
  name: ActionName;
  action: CategoryActionP;
}

type Handler = (
  item: IContextMenuInteractItem
) => Promise<CategoryCommand<void | Record<string, any>> | void>;
type Handlers = Record<string, Handler>;

class CommandBuilder {
  private readonly handlers: Handlers;
  private actionTypes: ActionType[];
  private readonly intlService: IIntlService;
  private readonly schemaRepository: ISchemaRepository;

  constructor(
    private items: IContextMenuItem[],
    private category: CategoryViewModel,
    private context: ClientContext,
    private datasetId: UniqueId,
    private actions: NamedAction[],
    private datasetsState: Record<UniqueId, DatasetCurrentState>,
    private hooks: ActionName[],
    private application: Nullable<TApplicationName>
  ) {
    this.schemaRepository = new CachedSchemaRepository();
    this.intlService = new IntlService();
    this.handlers = {
      Filter: this.buildFilterCommand.bind(this),
      FilterByColumn: this.buildFilterByColumnCommand.bind(this),
      ClearColumnsFilters: this.buildClearColumnsFiltersCommand.bind(this),
      Refresh: this.buildRefreshCommand.bind(this),
      Create: this.buildCreateCommand.bind(this),
      Duplicate: this.buildDuplicateCommand.bind(this),
      Delete: this.buildDeleteCommand.bind(this),
      Update: this.buildUpdateCommand.bind(this),
      Action: this.buildActionCommand.bind(this),
      Copy: this.buildCopyCommand.bind(this),
      Move: this.buildMoveCommand.bind(this),
      Import: this.buildImportCommand.bind(this),
      CSVExport: this.buildCSVExportCommand.bind(this),
      ExcelExport: this.buildExcelExportCommand.bind(this),
      Settings: this.buildSettingsCommand.bind(this),
      Toolbar: this.buildToolbarCommand.bind(this),
      Catalogs: this.buildCatalogsCommand.bind(this),
      Watch: this.buildWatchCommand.bind(this),
      Review: this.buildReviewCommand.bind(this),
      FileLinks: this.buildFileLinkCommand.bind(this),
      Download: this.buildDownloadCommand.bind(this),
      LinkedFiles: this.buildLinkedFilesCommand.bind(this),
      InputDocs: this.buildInputDocsCommand.bind(this),
      OutputDocs: this.buildOutputDocsCommand.bind(this),
      DocLinksGraph: this.buildDocLinksGraphCommand.bind(this),
      WeakenLinks: this.buildWeakenLinksCommand.bind(this),
      RestoreLinks: this.buildRestoreLinksCommand.bind(this),
      ActionHistory: this.buildActionHistoryCommand.bind(this),
      Reports: this.buildReportsCommand.bind(this),
    };

    this.actionTypes = Object.values(actions)
      .map(action => action.action.Type)
      // TODO: exclude action types with multiple instances
      .filter(value => value !== undefined) as ActionType[];
  }

  private get currentCategory(): CategoryName {
    return this.datasetsState[this.datasetId].source.category;
  }

  public async buildCommandsForDataset(): Promise<CommandComponent[]> {
    return this.buildCommands(this.items);
  }

  public async buildHooksForDisplayMode(): Promise<CommandComponent[]> {
    const commands: CommandComponent[] = [];

    for (const hook of this.hooks) {
      const actionSchema = await this.schemaRepository.getAction(
        this.context.get('MasterCategory'),
        hook
      );

      if (actionSchema.Type === 'ShowCategory') {
        const command = await this.buildShowCategoryCommand({
          name: hook,
        } as IContextMenuActionItem);

        if (command) {
          commands.push(new CommandItem(command));
        }
      } else {
        const command = new RunActionCommand(
          await this.intlService.getActionName(
            this.category.source.category,
            actionSchema.resId ?? actionSchema.Type
          ),
          new RunActionCommandImpl(
            this.context.get('MasterCategory'),
            hook,
            (await this.schemaRepository.getAction(
              this.context.get('MasterCategory'),
              hook
            )) as ExecutedActionP,
            this.category
          )
        );

        commands.push(new CommandItem(command));
      }
    }
    return commands;
  }

  private findAction(
    type: ActionType | void,
    name?: string | void
  ): NamedAction | void {
    let result: NamedAction | void = void 0;
    if (name) {
      result = this.actions.find(action => action.name === name);
    }
    if (!result && type && this.actionTypes.includes(type)) {
      result = this.actions.find(action => action.action.Type === type);
    }
    return result;
  }

  private findItemByKind(kind: TContextMenuItemKind): IContextMenuItem | void {
    return this.items.find(i => i.kind === kind);
  }

  private async buildCommands(
    items: IContextMenuItem[]
  ): Promise<CommandComponent[]> {
    const commands: CommandComponent[] = [];

    for (const item of items) {
      if ((item as IContextMenuInteractItem).hidden) {
        continue;
      }
      if (item.kind === 'Separator') {
        commands.push(await CommandBuilder.buildSeparator());
      } else if (item.kind === 'Group') {
        commands.push(await this.buildGroup(item as IContextMenuGroupItem));
      } else {
        const handler = this.handlers[item.kind];
        if (handler) {
          const command = await handler(item);
          if (command) {
            if (item.availability || item.badge) {
              commands.push(
                new CommandItem(new CommandDecorator(command, item) as any)
              );
            } else {
              commands.push(new CommandItem(command));
            }
          }
        }
      }
    }
    return commands;
  }

  private static async buildSeparator(): Promise<CommandComponent> {
    return new SeparatorItem();
  }

  private async buildGroup(
    item: IContextMenuGroupItem
  ): Promise<CommandComponent> {
    return new GroupItem(
      await this.buildCommands(item.items),
      await this.intlService.getMenuGroupTitle(this.currentCategory, item.name),
      item
    );
  }

  private async buildFilterCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    const displayMode = await this.schemaRepository.getDisplayMode(
      this.context.get('MasterCategory'),
      this.context.get('DisplayMode')
    );
    const datasetName = this.category.datasets[this.datasetId].name;
    const { conditions, source } = displayMode.datasets[datasetName];
    if (conditions) {
      const { Base } = await this.schemaRepository.getView(source);
      const category = await this.schemaRepository.getCategory(Base);
      const conditionSet = category[conditions.name];
      if (!conditionSet) {
        return;
      }

      return new FilterRecordsCommand(
        new FilterRecordsCommandImpl(
          (conditionSet as ConditionSetP).items,
          this.context.get('Category'),
          conditions.form
        )
      );
    }
  }

  private async buildFileLinkCommand(): Promise<
    CategoryCommand<void | Record<string, any>>
  > {
    return new FileLinkRecordCommand(new FileLinkRecordCommandImpl());
  }

  private async buildRefreshCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    return new UpdateDatasetCommand(new NoopCommandImpl());
  }

  private async buildCreateCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    const action = this.findAction(
      'Create',
      (item as IContextMenuItemOverride).action
    );
    if (action) {
      return new CreateRecordCommand(
        new CreateRecordCommandImpl(
          this.context.get('Category'),
          action.name,
          action.action as RegularActionP,
          this.category,
          undefined,
          item.refreshRules
        )
      );
    }
  }

  private async buildPrintCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    const action = await this.schemaRepository.getAction(
      this.context.get('Category'),
      item.name
    );

    return new PrintCommand(
      new PrintCommandImpl(
        this.currentCategory,
        item.name,
        action as PrintActionP,
        this.category,
        undefined,
        item.refreshRules
      ),
      await this.intlService.getActionName(
        this.currentCategory,
        item.resId ?? action.resId ?? item.name
      ),
      item.icon as IconName,
      item.menuResId
        ? await this.intlService.getActionName(
            this.currentCategory,
            item.menuResId
          )
        : undefined,
      item.hotKey
    );
  }

  private async buildDuplicateCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    let action = this.findAction(
      void 0,
      (item as IContextMenuItemOverride).action
    );
    if (!action) {
      const createItem = this.findItemByKind('Create');
      action = this.findAction(
        'Create',
        createItem ? (createItem as IContextMenuItemOverride).action : void 0
      );
    }
    if (action) {
      (action.action as ExecutedActionP).ProcessMode = 'Current';
      return new DuplicateRecordCommand(
        new RunActionCommandImpl(
          this.context.get('Category'),
          action.name,
          action.action as ExecutedActionP,
          this.category,
          undefined,
          item.refreshRules
        )
      );
    }
  }

  private async buildDeleteCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    const action = this.findAction(
      'Delete',
      (item as IContextMenuItemOverride).action
    );
    if (action) {
      return new DeleteRecordCommand(
        new DeleteRecordCommandImpl(
          new RunActionCommandImpl(
            this.context.get('Category'),
            action.name,
            action.action as ExecutedActionP,
            this.category,
            undefined,
            item.refreshRules
          )
        )
      );
    }
  }

  private async buildUpdateCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    const action = this.findAction(
      'Update',
      (item as IContextMenuItemOverride).action
    );
    if (action) {
      return new EditRecordCommand(
        new RunActionCommandImpl(
          this.context.get('Category'),
          action.name,
          action.action as ExecutedActionP,
          this.category,
          undefined,
          item.refreshRules
        )
      );
    }
  }

  private async buildReviewCommand(
    _item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    const displayMode = await this.schemaRepository.getDisplayMode(
      this.context.get('MasterCategory'),
      this.context.get('DisplayMode')
    );
    const datasetName = this.category.datasets[this.datasetId].name;
    const displayModeDesc = displayMode.datasets[datasetName];

    if (
      displayModeDesc.recordViews &&
      Object.keys(displayModeDesc.recordViews).length
    ) {
      return new ReviewRecordCommand(
        new ReviewRecordCommandImpl(this.context.get('Category'), 'Review')
      );
    }
  }

  private async buildDownloadCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    const action = (await this.schemaRepository.getAction(
      this.context.get('Category'),
      item.name
    )) as RegularActionP;
    const confirmation = tg.isActionWithConfirmationOptions(action)
      ? action.confirmation
      : {};
    return new DownloadCommand(
      new DownloadCommandImpl(
        new RunActionCommandImpl(
          this.context.get('Category'),
          item.name,
          action,
          this.category,
          undefined,
          item.refreshRules,
          confirmation.shortMessageId
            ? await this.intlService.getCategoryMessage(
                this.currentCategory,
                confirmation.shortMessageId
              )
            : undefined,
          confirmation.longMessageId
            ? await this.intlService.getCategoryMessage(
                this.currentCategory,
                confirmation.longMessageId
              )
            : undefined,
          item.icon
        )
      ),
      await this.intlService.getActionName(
        this.currentCategory,
        item.resId ?? action.resId ?? item.name
      ),
      item.icon as IconName,
      item.menuResId
        ? await this.intlService.getActionName(
            this.currentCategory,
            item.menuResId
          )
        : undefined,
      item.hotKey
    );
  }

  private async buildShowCategoryCommand(
    item: IContextMenuActionItem
  ): Promise<ShowCategoryCommand | void> {
    const action = await this.schemaRepository.getAction(
      this.context.get('Category'),
      item.name
    );

    if (action.Type !== 'ShowCategory') {
      return;
    }

    return new ShowCategoryCommand(
      new ShowCategoryCommandImpl(
        this.application,
        action,
        this.category,
        item.name,
        this.datasetId,
        item.refreshRules
      ),
      await this.intlService.getActionName(
        this.currentCategory,
        item.resId ?? action.resId ?? item.name
      ),
      item.icon as IconName,
      item.menuResId
        ? await this.intlService.getActionName(
            this.currentCategory,
            item.menuResId
          )
        : undefined,
      item.hotKey
    );
  }

  private async buildActionCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    if (
      this.context.get('Category') === 'ClientPostPerform' &&
      item.name === 'PostPerformSetPlanWorkType'
    ) {
      return this.buildOpenPlanWorkTableCommand(item.icon as IconName);
    }

    if (
      this.context.get('Category') === 'ClientPostPerform' &&
      item.name === 'PostPerformSetFactWorkType'
    ) {
      return this.buildOpenFactWorkTableCommand(item.icon as IconName);
    }

    if (
      this.context.get('Category') === 'ENCalcPeriods' &&
      item.name === 'WorkCalendarSetWorkDays'
    ) {
      return this.buildOpenWorkCalendarCommand();
    }

    if (
      this.context.get('Category') === 'TimeBoardsPerforms' &&
      item.name === 'TimeBoard'
    ) {
      return this.buildOpenTimeBoardCommand(item);
    }

    if (
      this.context.get('Category') === 'RolePrivs' &&
      item.name === 'showPrivsPanes'
    ) {
      return this.buildOpenRolePrivsCommand();
    }

    const action = await this.schemaRepository.getAction(
      this.context.get('Category'),
      item.name
    );

    if (action.Type === 'ShowCategory') {
      return this.buildShowCategoryCommand(item as IContextMenuActionItem);
    } else if (action.Type === 'Print') {
      return this.buildPrintCommand(item);
    } else if (action.Type === 'Pivot') {
      return this.buildPivotCommand(item);
    } else if (action.Type === 'Download') {
      return this.buildDownloadCommand(item);
    } else if (action.Type === SIGN_ACTION_TYPE) {
      return this.buildSignCommand(item, action);
    }
    const confirmation = tg.isActionWithConfirmationOptions(action)
      ? action.confirmation
      : {};
    return new RunActionCommand(
      await this.intlService.getActionName(
        this.currentCategory,
        item.resId ?? action.resId ?? item.name
      ),
      new RunActionCommandImpl(
        this.context.get('Category'),
        item.name,
        action as ExecutedActionP,
        this.category,
        undefined,
        item.refreshRules,
        confirmation.shortMessageId
          ? await this.intlService.getCategoryMessage(
              this.currentCategory,
              confirmation.shortMessageId
            )
          : undefined,
        confirmation.longMessageId
          ? await this.intlService.getCategoryMessage(
              this.currentCategory,
              confirmation.longMessageId
            )
          : undefined,
        item.icon
      ),
      item.icon as IconName,
      item.menuResId
        ? await this.intlService.getActionName(
            this.currentCategory,
            item.menuResId
          )
        : undefined,
      item.hotKey
    );
  }

  private async buildOpenPlanWorkTableCommand(
    icon?: IconName
  ): Promise<CategoryCommand<void>> {
    return new OpenPlanWorkTableCommand(new NoopCommandImpl(), icon);
  }

  private async buildOpenFactWorkTableCommand(
    icon?: IconName
  ): Promise<CategoryCommand<void>> {
    return new OpenFactWorkTableCommand(new NoopCommandImpl(), icon);
  }

  private async buildOpenWorkCalendarCommand(): Promise<CategoryCommand<void>> {
    return new OpenWorkCalendarCommand(new NoopCommandImpl());
  }

  private async buildOpenTimeBoardCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void>> {
    const action = await this.schemaRepository.getAction(
      this.context.get('Category'),
      item.name
    );
    return new OpenTimeBoardCommand(
      new NoopCommandImpl(),
      await this.intlService.getActionName(
        this.currentCategory,
        item.resId ?? (action.resId as string)
      )
    );
  }

  private async buildOpenRolePrivsCommand(): Promise<CategoryCommand<void>> {
    return new OpenRolePrivsCommand(new NoopCommandImpl());
  }

  private async buildMoveCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    if (this.category.treeType !== CategoryTreeType.NONE) {
      return new MoveRecordCommand(
        new MoveRecordCommandImpl(this.context.get('Category'))
      );
    }
  }

  private async buildCopyCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    return new CopyCommand(new NoopCommandImpl());
  }

  private async buildImportCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    // not implemented
  }

  private async buildCSVExportCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    return new CSVExportCommand(new NoopCommandImpl());
  }

  private async buildFilterByColumnCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    return new FilterByColumnCommand(new NoopCommandImpl());
  }

  private async buildClearColumnsFiltersCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    return new ClearColumnsFiltersCommand(new NoopCommandImpl());
  }

  private async buildExcelExportCommand(): Promise<CategoryCommand<void> | void> {
    return new ExcelExportCommand(new NoopCommandImpl());
  }

  private async buildSettingsCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    return new SettingsCommand(new SettingsCommandImpl());
  }

  private async buildToolbarCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    return new ToolbarCommand(new NoopCommandImpl());
  }

  private async buildCatalogsCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    if (this.category.treeType === CategoryTreeType.CATALOGS) {
      return new CatalogsCommand(new NoopCommandImpl());
    }
  }

  private async buildWatchCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    return new WatchCommand(new NoopCommandImpl());
  }

  private async buildLinkedFilesCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    const category = await this.schemaRepository.getCategoryExtendedProperties(
      this.currentCategory
    );
    if (category.linkedFiles) {
      return new LinkedFilesCommand(new LinkedFilesCommandImpl());
    }
  }

  private async buildInputDocsCommand(): Promise<
    CategoryCommand<void | Record<string, any>>
  > {
    return new InputDocsCommand(new NoopCommandImpl());
  }

  private async buildOutputDocsCommand(): Promise<
    CategoryCommand<void | Record<string, any>>
  > {
    return new OutputDocsCommand(new NoopCommandImpl());
  }

  private async buildDocLinksGraphCommand(): Promise<
    CategoryCommand<void | Record<string, any>>
  > {
    return new DocLinksGraphCommand(new NoopCommandImpl());
  }

  private async buildRestoreLinksCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    const action = this.findAction(
      void 0,
      (item as IContextMenuItemOverride).action
    );
    if (action) {
      (action.action as unknown as ActionWithConfirmation).confirmation = {
        hideConfirmation: true,
      };
      return new RestoreLinksCommand(
        new RunActionCommandImpl(
          this.context.get('Category'),
          action.name,
          action.action as ExecutedActionP,
          this.category,
          undefined,
          item.refreshRules
        )
      );
    }
  }

  private async buildWeakenLinksCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    const action = this.findAction(
      void 0,
      (item as IContextMenuItemOverride).action
    );
    if (action) {
      (action.action as unknown as ActionWithConfirmation).confirmation = {
        hideConfirmation: true,
      };
      return new WeakenLinksCommand(
        new RunActionCommandImpl(
          this.context.get('Category'),
          action.name,
          action.action as ExecutedActionP,
          this.category,
          undefined,
          item.refreshRules
        )
      );
    }
  }

  private async buildPivotCommand(
    item: IContextMenuInteractItem
  ): Promise<CategoryCommand<void | Record<string, any>> | void> {
    const actionSchema = await this.schemaRepository.getAction(
      this.context.get('Category'),
      item.name
    );
    return new PivotCommand(
      new PivotReportCommandImpl(
        this.context.get('Category'),
        item.name,
        actionSchema as ExecutedActionP,
        this.category,
        undefined,
        item.refreshRules
      ),
      await this.intlService.getActionName(
        this.currentCategory,
        item.resId ?? actionSchema.resId ?? item.name
      ),
      item.icon as IconName,
      item.menuResId
        ? await this.intlService.getActionName(
            this.currentCategory,
            item.menuResId
          )
        : undefined,
      item.hotKey
    );
  }

  private async buildActionHistoryCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    const category = await this.schemaRepository.getCategoryExtendedProperties(
      this.currentCategory
    );
    if (category.actionHistory) {
      return new ActionHistoryCommand(new ActionHistoryCommandImpl());
    }
  }

  private async buildSignCommand(
    item: IContextMenuInteractItem,
    actionSchema: SignActionP
  ): Promise<CategoryCommand<void | Record<string, any>> | undefined> {
    const actionTitle = await this.intlService.getCategoryFieldTitle(
      this.currentCategory,
      actionSchema.resId ?? item.name
    );
    return new SignCommand(
      new SignCommandImpl(
        this.context.get('Category'),
        item.name,
        actionSchema,
        this.category
      ),
      actionTitle
    );
  }

  private async buildReportsCommand(): Promise<CategoryCommand<void | Record<
    string,
    any
  >> | void> {
    const action = this.findAction('Print');
    if (action) {
      return new ReportsCommand(new ReportsCommandImpl(this.datasetId));
    }
  }
}

export default CommandBuilder;
